import React, { useState } from "react";
import { Modal, Button, Typography, Table, Empty, Space } from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import { CloseOutlined } from "@ant-design/icons";
import Order from "../../orderDetails/index";
import { getOrder } from "../../../api/server";
import { useSelector } from "react-redux";
import { EyeOutlined } from "@ant-design/icons";
import { useLocation } from "react-router-dom";

const { Title, Text } = Typography;

const ImportResultsModal = ({ visible, onClose, results }) => {
  const intl = useIntl();
  let location = useLocation();
  const isAdmin = location.pathname.includes("admin");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const scopePrefix = useSelector((state) => state.user.scopePrefix);

  const columns = [
    {
      title: intl.formatMessage({ id: "app.ordersImport.line_number" }),
      dataIndex: "line_number",
      key: "line_number",
      render: (line_number) => (
        <Text>{typeof line_number === "number" ? line_number + 1 : ""}</Text>
      ),
    },
    {
      title: intl.formatMessage({ id: "app.ordersImport.full_name" }),
      dataIndex: ["content"],
      key: "full_name",
      render: (content, record) => {
        const data = JSON.parse(content);
        const patientId = record?.patient_id;
        const linkPath = isAdmin
          ? `/admin/patients/${patientId}`
          : `${scopePrefix}patients/${patientId}`;

        return patientId ? (
          <a
            href={linkPath}
            style={{ textDecoration: "underline", color: "#000000" }}
          >
            <div className="content">{data.full_name?.trim()}</div>
          </a>
        ) : (
          <div className="content">{data.full_name?.trim()}</div>
        );
      },
    },
    {
      title: intl.formatMessage({ id: "app.ordersImport.id_number" }),
      dataIndex: ["content"],
      key: "local_id_number",
      render: (content, record) => {
        const data = JSON.parse(content);
        const patientId = record?.patient_id;
        const linkPath = isAdmin
          ? `/admin/patients/${patientId}`
          : `${scopePrefix}patients/${patientId}`;
        return patientId ? (
          <a
            href={linkPath}
            style={{ textDecoration: "underline", color: "#000000" }}
          >
            <div className="content">{data.local_id_number}</div>
          </a>
        ) : (
          <div className="content">{data.local_id_number}</div>
        );
      },
    },
    {
      title: intl.formatMessage({
        id: "app.ordersImport.commitment_number",
      }),
      dataIndex: ["content"],
      key: "commitment_number",
      render: (content) => {
        const data = JSON.parse(content);
        return data.commitment_number;
      },
    },
    {
      title: intl.formatMessage({
        id: "app.ordersImport.action",
      }),
      dataIndex: ["action"],
      key: "action",
      render: (content) => {
        return content
          ? `${intl.formatMessage({ id: `app.ordersImport.${content}` })}`
          : "";
      },
    },
    {
      title: intl.formatMessage({ id: "app.ordersImport.period" }),
      dataIndex: ["content"],
      key: "period",
      render: (_content, record) => {
        const startDate = new Date(
          record?.order?.effective_from
        ).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
        const endDate = record?.order?.effective_to
          ? new Date(record?.order?.effective_to).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })
          : intl.formatMessage({
              id: "app.ordersImport.no_end_date",
            });
        return record.order_id && `${endDate} - ${startDate}`;
      },
      width: 200,
    },
    {
      title: intl.formatMessage({ id: "app.ordersImport.hours" }),
      dataIndex: ["content"],
      key: "hours",
      render: (content) => {
        const data = JSON.parse(content);
        return data.hours_per_week;
      },
    },
    {
      title: intl.formatMessage({ id: "app.ordersImport.status" }),
      dataIndex: "success",
      key: "success",
      render: (success) => (
        <Text type={success ? "success" : "danger"}>
          <FormattedMessage
            id={success ? "app.ordersImport.success" : "app.ordersImport.error"}
          />
        </Text>
      ),
    },
    {
      title: intl.formatMessage({ id: "app.ordersImport.error" }),
      dataIndex: "error",
      key: "error",
      render: (error) =>
        error ? (
          <Text type="danger" style={{ fontSize: "0.9em" }}>
            {intl.formatMessage({ id: `app.ordersImport.${error}` })}
          </Text>
        ) : null,
    },
    {
      title: intl.formatMessage({
        id: "app.ordersImport.actions",
      }),
      width: "130px",
      render: (_, record) =>
        record?.order_id && (
          <Space size="middle">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => onEditClicked(record)}
            >
              {intl.formatMessage({
                id: "app.ordersImport.viewOrder",
              })}
            </Button>
          </Space>
        ),
    },
  ];

  const onEditClicked = async (record) => {
    const params = {
      scopePrefix,
      orderId: record?.order_id,
      patientId: record?.order?.patient_id,
    };
    const order = await getOrder(params);
    setSelectedOrder(order?.data || null);
  };

  return (
    <Modal
      closable={false}
      maskClosable={false}
      open={visible}
      footer={null}
      onCancel={onClose}
      title={null}
      width={"fit-content"}
      styles={{
        body: {
          padding: "20px 100px",
        },
      }}
      destroyOnClose={true}
    >
      <div>
        <div className="header" style={{ height: "40px" }}>
          <Button
            style={{ position: "absolute", left: 10, top: 10 }}
            className="closeBtn"
            type="text"
            shape="circle"
            icon={<CloseOutlined />}
            onClick={onClose}
          />
        </div>
        <Title
          style={{
            fontSize: "30px",
            fontWeight: 500,
            lineHeight: 1.27,
            color: "rgba(0, 0, 0, 0.85)",
            margin: "13px 0 40px 0",
            textAlign: "center",
          }}
        >
          <FormattedMessage id="app.ordersImport.tableTitle" />
        </Title>

        {results.length === 0 ? (
          <Empty
            description={
              <FormattedMessage id="app.ordersImport.no_results_found" />
            }
          />
        ) : (
          <div style={{ padding: "20px 0" }}>
            <Table
              dataSource={results}
              columns={columns}
              rowKey="id"
              pagination={true}
              scroll={{ y: 400 }}
              size="small"
              bordered
            />
            <div style={{ marginTop: 16, textAlign: "center" }}>
              <Space>
                <Button onClick={onClose} type="primary">
                  <FormattedMessage id="app.actions.close" />
                </Button>
              </Space>
            </div>
          </div>
        )}
      </div>
      <Order
        type={"edit"}
        onHide={() => setSelectedOrder(null)}
        onConfirm={() => setSelectedOrder(null)}
        selectedOrder={selectedOrder}
        visible={selectedOrder}
      />
    </Modal>
  );
};

export default ImportResultsModal;
